import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../../ui/input';
import { cn } from '../../../lib/utils';
import { backendUrl } from '../gameplanUtils';
import { HealthPill, PriorityPill } from '../credit/ActionQueue';
import { money, postEvent } from '../credit/creditApi';

const fmtD = (v) => (v ? new Date(v).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : '—');
const daysTo = (v) => { if (!v) return null; const d = new Date(v); const t = new Date(); d.setHours(0, 0, 0, 0); t.setHours(0, 0, 0, 0); return Math.round((d - t) / 86400000); };

export const useFiles = (authToken, refreshSignal) => {
  const [files, setFiles] = useState(null);
  const [paused, setPaused] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await fetch(`${backendUrl}/api/gameplan/files`, { headers: { Authorization: `Bearer ${authToken}` } });
      const d = await r.json();
      setFiles(d.files || []);
      setPaused(!!d.credit_paused);
    } catch { toast.error('Failed to load files'); }
  }, [authToken]);
  useEffect(() => { load(); }, [load, refreshSignal]);
  return [files, load, paused];
};

/* One row vocabulary for every list in the Tracker: Health · Client · Stage · Lender · Broker · Credit · Next · date · actions */
export const FileRow = ({ f, onOpen, dateLabel, dateValue, actions, selected, onSelect, testid }) => {
  const s = f.file;
  return (
    <div onClick={() => onOpen(f)} data-testid={testid || `file-row-${f.id}`}
      className={cn('grid grid-cols-1 lg:grid-cols-[28px_110px_minmax(170px,1.3fr)_minmax(140px,1fr)_100px_100px_110px_minmax(200px,1.6fr)_110px_170px] lg:gap-3 px-4 py-3 items-center border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer',
        s.priority === 'URGENT' && 'border-l-[3px] border-l-rose-500', s.health === 'STALE' && 'bg-slate-50/60')}>
      <div onClick={(e) => e.stopPropagation()} className="hidden lg:block">{onSelect && <input type="checkbox" checked={!!selected} onChange={() => onSelect(f.id)} className="w-4 h-4 accent-slate-900" data-testid={`file-select-${f.id}`} />}</div>
      <div><HealthPill h={s.health} /></div>
      <div className="min-w-0"><div className="font-semibold text-slate-900 truncate">{f.client_name}</div><div className="text-[11px] text-slate-500 tabular-nums">{money(f.est_loan_amount)} · {f.loan_purpose || f.loan_type}</div></div>
      <div className="text-xs text-slate-800 truncate"><div className="font-semibold">{s.stage_label}</div><div className="text-slate-500">{s.status_label}{s.in_stage && s.health !== 'STALE' ? ` · ${s.in_stage}` : ''}</div></div>
      <div className="text-xs text-slate-700 truncate hidden lg:block">{f.lender || '—'}</div>
      <div className="text-xs text-slate-700 truncate hidden lg:block">{f.broker || '—'}</div>
      <div className={cn('text-xs truncate hidden lg:block', s.owner ? 'text-slate-700' : 'text-slate-400')}>{s.owner || 'Unassigned'}</div>
      <div className="text-xs min-w-0">
        {s.action ? <span className="inline-flex items-center gap-1.5 font-bold text-slate-900 truncate"><PriorityPill p={s.priority} /> {s.action}</span>
          : s.waiting ? <span className="text-slate-600">Waiting on <b>{s.waiting}</b>{s.waiting_days != null ? ` · ${s.waiting_days}d` : ''}{s.waiting_what ? ` — ${s.waiting_what}` : ''}</span>
          : s.health === 'STALE' ? <span className="text-slate-500">Not updated since entered — needs triage</span> : <span className="text-slate-400">Nothing outstanding</span>}
      </div>
      <div className="text-xs text-slate-600 hidden lg:block"><span className="text-slate-400">{dateLabel} </span>{dateValue}</div>
      <div className="lg:text-right mt-2 lg:mt-0 flex lg:justify-end gap-1.5 flex-wrap" onClick={(e) => e.stopPropagation()}>{actions}</div>
    </div>
  );
};

export const RowBtn = ({ children, onClick, tone = 'dark', testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={cn('px-2.5 py-1 rounded-full text-[11px] font-bold transition-colors', tone === 'dark' ? 'bg-slate-900 text-white hover:bg-slate-700' : tone === 'lime' ? 'bg-lime-500 text-slate-900 hover:bg-lime-400' : tone === 'danger' ? 'border border-rose-300 text-rose-700 hover:bg-rose-50' : 'border border-slate-300 text-slate-800 hover:bg-slate-100')}>{children}</button>
);

export const ListHeader = ({ dateLabel, selectAll }) => (
  <div className="hidden lg:grid grid-cols-[28px_110px_minmax(170px,1.3fr)_minmax(140px,1fr)_100px_100px_110px_minmax(200px,1.6fr)_110px_170px] gap-3 px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-100">
    <span>{selectAll}</span><span>Health</span><span>Client</span><span>Stage · status</span><span>Lender</span><span>Broker</span><span>Credit</span><span>Next action / waiting</span><span>{dateLabel}</span><span className="text-right">Actions</span>
  </div>
);

export const TriageButtons = ({ f, authToken, onDone }) => {
  const run = async (outcome) => {
    try { await postEvent(authToken, f.id, 'triage', { outcome }); toast.success(`${f.client_name}: ${outcome.replace('_', ' ')}`); onDone(); } catch (e) { toast.error(e.message); }
  };
  return (<>
    <RowBtn tone="light" onClick={() => run('active')} testid={`triage-active-${f.id}`}>Still active</RowBtn>
    {f.status_stage !== 'approved' && <RowBtn tone="lime" onClick={() => run('approved')} testid={`triage-approved-${f.id}`}>Approved</RowBtn>}
    {f.status_stage === 'approved' && <RowBtn tone="lime" onClick={() => run('settled')} testid={`triage-settled-${f.id}`}>Settled</RowBtn>}
    <RowBtn tone="danger" onClick={() => run('not_proceeding')} testid={`triage-np-${f.id}`}>Not proceeding</RowBtn>
  </>);
};

export const GroupBlock = ({ title, sub, count, value, children, testid, tone }) => (
  <section className="rounded-xl bg-white border border-slate-200 overflow-hidden" data-testid={testid}>
    <div className={cn('flex items-baseline justify-between px-4 py-2.5 border-b border-slate-100', tone === 'muted' && 'bg-slate-50')}>
      <div><span className="text-sm font-extrabold text-slate-900">{title}</span>{sub && <span className="ml-2 text-xs text-slate-500">{sub}</span>}</div>
      <div className="text-xs text-slate-600 tabular-nums"><b className="text-slate-900">{count}</b> files · {money(value)}</div>
    </div>
    {children}
  </section>
);

export const SearchBox = ({ q, setQ, testid }) => (
  <div className="relative max-w-sm flex-1"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client, lender, broker, credit owner…" className="pl-9 h-9 bg-white" data-testid={testid} /></div>
);

export const filterFiles = (files, q) => {
  const s = q.trim().toLowerCase();
  return !s ? files : files.filter((f) => [f.client_name, f.lender, f.broker, f.file.owner, f.file.stage_label, f.file.action].some((v) => (v || '').toLowerCase().includes(s)));
};

export const Spinner = () => <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
export { fmtD, daysTo, money };
